import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { TrendingUp, Package, Truck, Megaphone, ArrowRight } from 'lucide-react';
import { services } from '../mock';

const Services = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  const icons = [
    <TrendingUp className="w-12 h-12" />,
    <Megaphone className="w-12 h-12" />,
    <Package className="w-12 h-12" />,
    <Truck className="w-12 h-12" />,
  ];

  return (
    <>
      <Helmet>
        <title>Global Trading & Procurement Services | Eaglesway Group</title>
        <meta 
          name="description" 
          content="Comprehensive trading, procurement, logistics and business development services from Eaglesway Group across UAE, UK and USA." 
        />
      </Helmet>
      
      <div className="min-h-screen bg-black text-white pt-20">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1773126377223-d72cb000382d"
            alt="Services background"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1
            className={`text-4xl md:text-5xl font-bold mb-6 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            Our <span className="text-[#D4AF37]">Services</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Comprehensive business solutions tailored to your global needs
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card
                key={service.id}
                className={`bg-gradient-to-br from-gray-900 to-black border-gray-800 hover:border-[#D4AF37] transition-all duration-500 transform hover:-translate-y-2 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className="p-8">
                  <div className="text-[#D4AF37] mb-6">{icons[index]}</div>
                  <h3 className="text-2xl font-bold text-white mb-4">
                    {service.title}
                  </h3>
                  <p className="text-gray-400 text-lg leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Info Section */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1773126379692-342ff516ea53"
                alt="Logistics services"
                className="rounded-lg shadow-2xl"
              />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Why Choose <span className="text-[#D4AF37]">Eaglesway</span>?
              </h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-[#D4AF37] rounded-full mt-2 mr-4"></div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      Global Network
                    </h3>
                    <p className="text-gray-400">
                      Strategic presence across UAE, UK, and USA for seamless international operations
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-[#D4AF37] rounded-full mt-2 mr-4"></div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      Reliable Partnership
                    </h3>
                    <p className="text-gray-400">
                      Trusted by businesses worldwide for quality products and professional service
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-[#D4AF37] rounded-full mt-2 mr-4"></div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      End-to-End Solutions
                    </h3>
                    <p className="text-gray-400">
                      From sourcing to delivery, we handle every aspect of your supply chain
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Get <span className="text-[#D4AF37]">Started?</span>
          </h2>
          <p className="text-gray-300 text-lg mb-8">
            Let us know your requirements and we'll provide a tailored solution for your business
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/quote')}
              className="bg-[#D4AF37] hover:bg-[#B8941F] text-black font-semibold px-10 py-7 text-lg transition-all duration-300"
            >
              Request a Quote
              <ArrowRight className="ml-2" />
            </Button>
            <Button
              onClick={() => navigate('/contact')}
              variant="outline"
              className="border-2 border-[#D4AF37] text-[#D4AF37] bg-transparent hover:bg-[#D4AF37] hover:text-black font-semibold px-10 py-7 text-lg transition-all duration-300"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Services;
